// ============================================================================
// UNIVERSAL ACTION HANDLERS - COMMUNICATION ACTIONS
// ============================================================================

import type { UniversalActionHandlers } from "./types";
import { debugModalData } from "./types";
import {
  extractCompanyId,
  hasContactInfo,
} from "@/utils/entity-type-guards";
import { getBrowserSupabaseClient } from "@wtc/supabase";
import { MODAL_IDS } from "@wtc/ui";
import { buildCompanyModalData } from "./utils/company-modal";
import { selfAssign } from "./utils/self-assign";

/**
 * Communication Actions
 */
export const communicationActions: UniversalActionHandlers = {
  // Send email action — contact fast path only (company/deal/etc. use manifest resolver)
  sendEmail: async (item, context) => {
    const entityName = context?.pageConfig?.entityName;
    const companyId =
      context?.entityIds?.companyId ??
      extractCompanyId(item as Record<string, unknown>) ??
      (typeof item.company_id === "string" ? item.company_id : undefined);

    const data = {
      item,
      customerEmail: item.email || item.contact_email,
      customerName: item.name || item.contact_name,
      companyId,
    };

    if (entityName) {
      context.onCloseModal(`${entityName}-detail`);
    }

    debugModalData("sendEmail", "compose-message", data);
    context.onOpenModal(MODAL_IDS.messages.compose, data);
  },

  // Send text message action - opens text message modal
  sendTextMessage: async (item, context) => {
    const data = {
      item,
      entityType: context.pageConfig?.entityName || "contact",
      recipientPhone: item.phone || item.contact_phone,
      recipientName: item.name || item.contact_name,
    };
    debugModalData("sendTextMessage", MODAL_IDS.messages.sendText, data);
    context.onOpenModal(MODAL_IDS.messages.sendText, data);
  },

  // Send text from a TEMPLATE — opens the template-capable send-text modal.
  // Used by deal/task row actions. The recipient is picked in the modal (and
  // pre-seeded from the row's contact when one is directly linked).
  sendTextTemplate: async (item, context) => {
    const row = item as Record<string, unknown>;
    const entity = context.pageConfig?.entityName;
    const initialContactId =
      typeof row.contact_id === "string" ? row.contact_id : undefined;
    context.onOpenModal(MODAL_IDS.messages.sendText, {
      pickContact: true,
      initialContactId,
      item,
      entityType: entity,
    });
    // Opening a modal is not "completion" — emit our own feedback so the
    // generic action runner doesn't fire a misleading success toast.
    context.onToast("Opening text composer…", "info");
  },

  // Create meeting action - opens meeting creation modal.
  // Modal is mounted globally by CreateMeetingModalContainer (DetailPageModals).
  scheduleMeeting: async (item, context) => {
    context.onOpenModal("create-meeting", {
      customerEmail: item.email || item.contact_email,
      customerName: item.name || item.contact_name,
      item,
    });
    // Emit our own feedback so executeDetailAction does NOT fire its generic
    // "Schedule meeting completed" toast — opening a modal is not completion.
    context.onToast("Opening meeting scheduler…", "info");
  },

  // Create task action - opens task creation modal
  createTask: async (item, context) => {
    context.onOpenModal("create-tasks", {
      contactId: item.contact_id || item.id,
      ticketId: item.ticket_id,
      item,
    });
  },

  // Make ticket action - opens ticket creation modal
  makeTicket: async (item, context) => {
    context.onOpenModal("create-ticket", {
      contactId: item.contact_id || item.id,
      companyId: item.company_id,
      item,
    });
  },

  // Make contact action - opens contact creation modal
  makeContact: async (item, context) => {
    context.onOpenModal("create-contact", {
      companyId: item.company_id || item.id,
      item,
    });
  },

  // Enroll support group action - opens the Google support-group enroll modal
  // preset to this company (design doc §2.4).
  enrollSupportGroup: async (item, context) => {
    context.onOpenModal(MODAL_IDS.companies.enrollSupportGroup, {
      companyId: item.id,
      companyName: item.name,
    });
    context.onToast("Opening support-group enrollment…", "info");
  },

  // Make task action - opens task creation modal
  makeTask: async (item, context) => {
    context.onOpenModal("create-tasks", {
      contactId: item.contact_id || item.id,
      ticketId: item.ticket_id,
      item,
    });
  },

  // Log time entry action - opens time entry creation modal
  // Passes ticket context to time entry creation modal
  logTimeEntry: async (item, context) => {
    // Determine if this is a ticket entity (tickets have title and/or ticket_number)
    const isTicketEntity = !!(item.title || item.ticket_number);

    // Extract ticket ID:
    // - If item is a ticket itself, use item.id
    // - If item has ticket_id (e.g., time entry, task), use item.ticket_id
    // - Otherwise fallback to item.id
    const ticketId = isTicketEntity ? item.id : item.ticket_id || item.id;

    // Extract ticket title from various possible fields
    const ticketTitle =
      item.title ||
      item.ticket_title ||
      (item.ticket_number ? `Ticket #${item.ticket_number}` : undefined);

    // Extract contact information - supports both flat and nested structures
    const contactId = item.contact_id;
    const contactName =
      item.contact_name ||
      item.name ||
      (item.contact ? item.contact.name || item.contact.email : undefined);

    // Extract company information - supports both flat and nested structures
    const companyId = item.company_id;
    const companyName = item.company_name || (item.company ? item.company.name : undefined);

    const data = {
      ticketId,
      ticketTitle,
      contactId,
      contactName,
      companyId,
      companyName,
      item,
    };
    debugModalData("logTimeEntry", "create-time-entry", data);
    context.onOpenModal("create-time-entry", data);
  },

  // NOTE: there is intentionally no `addNote` handler. No action definition
  // ever wired it, and the "add-note" modal id it opened was never mounted —
  // a silent dead end. Task notes go through `addTaskNote` (entity-actions-misc),
  // which opens the mounted "task-add-note" modal.

  // Add update action - opens the add-update modal
  // (AddTicketUpdateModalContainer mounted in DetailPageModals).
  addUpdate: async (item, context) => {
    context.onOpenModal("add-update", {
      ticketId: item.ticket_id || item.id,
      item,
    });
  },

  // Log activity action - opens log activity modal with contact ID in URL
  logActivity: async (item, context) => {
    const row = item as Record<string, unknown>;
    const surface = (context as { surfaceEntityType?: string }).surfaceEntityType;
    const enrichedContact = row.effectiveLogActivityContactId as string | undefined;
    let contactId =
      enrichedContact ||
      (typeof row.contact_id === "string" ? row.contact_id : undefined) ||
      undefined;

    if (!contactId && row.entity_type && row.entity_id) {
      const et = String(row.entity_type).trim().toLowerCase();
      if (et === "contacts" || et === "contact") {
        contactId = String(row.entity_id);
      }
    }

    if (!contactId && surface !== "task") {
      contactId = (row.contact_id as string | undefined) || String(row.id);
    }

    context.onOpenModal("log-activity", {
      contactId,
      ticketId: row.ticket_id as string | undefined,
      item,
    });
  },

  // Initiate call action — opens send-text modal (Quo call inside modal)
  initiateCall: async (item, context) => {
    const phone = item.phone || item.contact_phone;
    if (phone) {
      context.onOpenModal(MODAL_IDS.messages.sendText, {
        item,
        entityType: context.pageConfig?.entityName || "contacts",
        recipientPhone: phone,
        recipientName: item.name || item.contact_name,
        initialContactId:
          typeof item.id === "string" && context.pageConfig?.entityName === "contacts"
            ? item.id
            : typeof item.contact_id === "string"
              ? item.contact_id
              : undefined,
      });
    } else {
      context.onToast("No phone number available", "warning");
    }
  },

  // Research company action - opens research company modal
  openResearchCompanyModal: async (item, context) => {
    context.onOpenModal("research-company", buildCompanyModalData(item, context));
  },

  // Find decision makers action - opens find decision makers modal
  openFindDecisionMakersModal: async (item, context) => {
    context.onOpenModal("find-decision-makers", buildCompanyModalData(item, context));
  },

  /**
   * Communications: set logged-in user as `user_id` (sales / activity attribution).
   * DB column is `user_id`; not the same as Postgres `created_at` author.
   */
  claimCommunicationAttribution: selfAssign({
    field: "user_id",
    mode: "scalar",
    guard: (row, context) => {
      if (typeof row.id !== "string" || typeof row.user_id !== "string") {
        context.onToast("This action only applies to communications.", "warning");
        return false;
      }
      return true;
    },
    messages: {
      alreadySet: "This communication is already attributed to you.",
      success: "You’re now attributed for this communication.",
    },
  }),

  reassignCommunicationSalesAttribution: async (item, context) => {
    const row = item as Record<string, unknown>;
    if (typeof row.id !== "string" || typeof row.user_id !== "string") {
      context.onToast("This action only applies to communications.", "warning");
      return;
    }

    context.onOpenModal(MODAL_IDS.communications.reassignSalesAttribution, {
      communicationId: row.id,
      currentUserId: row.user_id,
    });
  },

  /**
   * File a meeting communication's summary into the company's
   * `03_Joint-Meetings` Google Drive subfolder. Mirrors the Slack action;
   * the heavy lifting lives in the server route, which calls the shared
   * `fileMeetingSummaryToJointMeetings` service.
   */
  saveMeetingToDrive: async (item, context) => {
    const row = item as Record<string, unknown>;
    if (typeof row.id !== "string") {
      context.onToast("This action only applies to communications.", "warning");
      return;
    }
    if (typeof row.company_id !== "string" || !row.company_id) {
      context.onToast(
        "This meeting isn't linked to a company, so it can't be filed to Drive.",
        "warning",
      );
      return;
    }

    try {
      const supabase = getBrowserSupabaseClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        context.onToast("Not authenticated", "error");
        return;
      }

      context.onToast("Filing meeting summary to Drive…", "info");

      const res = await fetch(
        `/api/communications/${encodeURIComponent(row.id)}/save-to-drive`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        },
      );
      const result = (await res.json()) as {
        ok?: boolean;
        webViewLink?: string | null;
        error?: string;
      };

      if (!res.ok || !result.ok) {
        context.onToast(result.error || "Failed to file meeting to Drive.", "error");
        return;
      }

      context.onToast("Meeting summary filed to Google Drive.", "success");
    } catch (error) {
      console.error("Error filing meeting summary to Drive:", error);
      context.onToast("Failed to file meeting to Drive.", "error");
    }
  },
};
